/**
 * A tiny floating context menu (ADR 0034).
 *
 * Used for right-clicking a text note / wall label: shows a short list of
 * actions at the cursor and dismisses on any outside pointerdown, Escape, or
 * scroll. Only one menu exists at a time. Pure DOM, no framework.
 *
 * @param {number} clientX viewport x
 * @param {number} clientY viewport y
 * @param {Array<{label:string, onClick:()=>void, danger?:boolean}>} items
 */
let openMenu = null;

export function showContextMenu(clientX, clientY, items) {
  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  for (const item of items) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `context-menu__item${item.danger ? ' context-menu__item--danger' : ''}`;
    btn.textContent = item.label;
    btn.setAttribute('role', 'menuitem');
    btn.addEventListener('click', () => {
      closeContextMenu();
      item.onClick();
    });
    menu.append(btn);
  }
  showPopover(clientX, clientY, menu, 'context-menu');
}

/**
 * The same floating surface with arbitrary content — used by the pen colour and
 * thickness pickers, which collapsed from eight always-visible toolbar buttons
 * into two (owner, 2026-07-29: every toolbar row costs drawing height on a
 * tablet).
 *
 * Positioning and dismissal (outside tap, Escape, scroll) are shared with the
 * context menu deliberately: two floating surfaces with two sets of dismissal
 * rules is how one of them ends up impossible to close.
 *
 * @param {number} clientX
 * @param {number} clientY
 * @param {HTMLElement} content
 * @param {string} [className] surface class; defaults to a plain popover
 */
export function showPopover(clientX, clientY, content, className = 'popover') {
  closeContextMenu();
  const surface = document.createElement('div');
  surface.className = className;
  surface.style.left = `${clientX}px`;
  surface.style.top = `${clientY}px`;
  surface.append(content);
  document.body.append(surface);
  openMenu = surface;

  // Keep it on-screen if it would overflow the right/bottom edge.
  const box = surface.getBoundingClientRect();
  if (box.right > window.innerWidth) surface.style.left = `${Math.max(6, window.innerWidth - box.width - 6)}px`;
  if (box.bottom > window.innerHeight) surface.style.top = `${Math.max(6, window.innerHeight - box.height - 6)}px`;

  // Dismissers, armed on the next tick so the opening event doesn't close it.
  setTimeout(() => {
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', closeContextMenu, true);
  }, 0);
  return surface;
}

function onOutside(e) {
  if (openMenu && !openMenu.contains(e.target)) closeContextMenu();
}
function onKey(e) {
  if (e.key === 'Escape') closeContextMenu();
}

export function closeContextMenu() {
  if (!openMenu) return;
  document.removeEventListener('pointerdown', onOutside, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('scroll', closeContextMenu, true);
  openMenu.remove();
  openMenu = null;
}
